import React, { useEffect, useState, useCallback } from 'react';
import api from '../api/client';
import { useAuth, hasRole } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { PageHeader } from '../components/PageHeader';
import { Plus, Edit2, Trash2 } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { formatDate, formatCurrency } from '../lib/format';
import { useToast } from '../hooks/use-toast';

const initialForm = { contract_id: '', invoice_no: '', billing_period_start: '', billing_period_end: '', amount: 0, status: 'pending', due_date: '', paid_date: '', remarks: '' };

const Billings = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [editing, setEditing] = useState(null);
  const canEdit = hasRole(user, 'super_admin','admin');

  const load = useCallback(async () => { setItems((await api.get('/billings')).data); setContracts((await api.get('/contracts')).data); }, []);
  useEffect(() => { load(); }, [load]);
  const ctrById = (id) => contracts.find((c) => c.id === id) || {};

  const save = async () => {
    try {
      const p = { ...form, amount: Number(form.amount) || 0 };
      if (editing) await api.put(`/billings/${editing.id}`, p);
      else await api.post('/billings', p);
      toast({ title: editing ? 'Updated' : 'Created' });
      setOpen(false); load();
    } catch (e) { toast({ title: 'Error', description: e.response?.data?.detail, variant: 'destructive' }); }
  };
  const remove = async (b) => { if (!window.confirm('Delete?')) return; await api.delete(`/billings/${b.id}`); load(); };

  return (
    <div className="space-y-4">
      <PageHeader title="Billing" description="Generate invoices and track contract billing."
        actions={canEdit && <Button onClick={() => { setEditing(null); setForm(initialForm); setOpen(true); }} className="bg-[hsl(217,91%,24%)] hover:bg-[hsl(217,91%,18%)]"><Plus className="h-4 w-4 mr-1.5" />New Invoice</Button>} />
      <Card><CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b text-left text-slate-600"><tr><th className="p-3">Invoice #</th><th className="p-3">Contract</th><th className="p-3">Period</th><th className="p-3">Amount</th><th className="p-3">Due Date</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th></tr></thead>
          <tbody className="divide-y">
            {items.length === 0 ? <tr><td colSpan={7} className="p-8 text-center text-slate-500">No invoices yet.</td></tr> :
              items.map((b) => { const c = ctrById(b.contract_id); return (
                <tr key={b.id} className="table-row-hover">
                  <td className="p-3 font-mono font-semibold">{b.invoice_no}</td>
                  <td className="p-3"><div className="font-mono text-xs">{c.contract_no}</div><div className="text-xs text-slate-500">{c.client_name}</div></td>
                  <td className="p-3 text-xs">{formatDate(b.billing_period_start)} – {formatDate(b.billing_period_end)}</td>
                  <td className="p-3 font-semibold">{formatCurrency(b.amount)}</td>
                  <td className="p-3 text-xs">{formatDate(b.due_date)}</td>
                  <td className="p-3"><Badge className={{pending:'bg-amber-100 text-amber-800 hover:bg-amber-100',paid:'bg-emerald-100 text-emerald-800 hover:bg-emerald-100',overdue:'bg-red-100 text-red-800 hover:bg-red-100'}[b.status]}>{b.status}</Badge></td>
                  <td className="p-3 text-right">{canEdit && <><Button size="sm" variant="ghost" onClick={() => { setEditing(b); setForm({ ...initialForm, ...b }); setOpen(true); }}><Edit2 className="h-4 w-4" /></Button><Button size="sm" variant="ghost" onClick={() => remove(b)}><Trash2 className="h-4 w-4 text-red-600" /></Button></>}</td>
                </tr>); })}
          </tbody>
        </table>
      </CardContent></Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>{editing ? 'Edit Invoice' : 'New Invoice'}</DialogTitle><DialogDescription>Billing invoice for a contract.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Contract *</Label>
              <Select value={form.contract_id} onValueChange={(v) => setForm({ ...form, contract_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select contract" /></SelectTrigger>
                <SelectContent>{contracts.map((c) => <SelectItem key={c.id} value={c.id}>{c.contract_no} – {c.client_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Invoice # *</Label><Input value={form.invoice_no} onChange={(e) => setForm({ ...form, invoice_no: e.target.value })} /></div>
            <div><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="pending">Pending</SelectItem><SelectItem value="paid">Paid</SelectItem><SelectItem value="overdue">Overdue</SelectItem></SelectContent></Select>
            </div>
            <div><Label>Billing Period Start</Label><Input type="date" value={form.billing_period_start || ''} onChange={(e) => setForm({ ...form, billing_period_start: e.target.value })} /></div>
            <div><Label>Billing Period End</Label><Input type="date" value={form.billing_period_end || ''} onChange={(e) => setForm({ ...form, billing_period_end: e.target.value })} /></div>
            <div><Label>Amount (PHP)</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
            <div><Label>Due Date</Label><Input type="date" value={form.due_date || ''} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
            <div><Label>Paid Date</Label><Input type="date" value={form.paid_date || ''} onChange={(e) => setForm({ ...form, paid_date: e.target.value })} /></div>
            <div className="col-span-2"><Label>Remarks</Label><Textarea rows={2} value={form.remarks || ''} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} className="bg-[hsl(217,91%,24%)] hover:bg-[hsl(217,91%,18%)]">{editing ? 'Update' : 'Create'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Billings;
